/**
 * client/src/features/income/hooks/useBuckets.js
 * 
 * Custom hook for managing income buckets.
 * Provides functions for fetching, creating, updating, and deleting buckets.
 */
import { useState, useCallback, useEffect } from 'react';
import bucketService from '../services/bucketService';

// Map MongoDB bucket types to frontend types (if needed)
const mapBucketType = (type) => {
  const typeMap = {
    'Household': 'HOUSEHOLD',
    'Business': 'BUSINESS', 
    'Investment': 'INVESTMENT',
    'Side Hustle': 'SIDE_HUSTLE',
    'Custom': 'CUSTOM'
  };
  
  // Return the mapped type or the original if not in the map
  return typeMap[type] || type;
};

// Convert MongoDB bucket to frontend format
const formatBucket = (bucket) => {
  if (!bucket) return null;
  
  return {
    _id: bucket._id,
    name: bucket.name,
    type: mapBucketType(bucket.type),
    order: bucket.order || 0,
    isExpanded: bucket.isExpanded !== undefined ? bucket.isExpanded : false,
    incomes: bucket.incomes || [],
    totalMonthlyIncome: bucket.totalMonthlyIncome || 0,
    totalYearlyIncome: bucket.totalYearlyIncome || 0,
    createdAt: bucket.createdAt,
    updatedAt: bucket.updatedAt
  };
};

/**
 * Hook for managing buckets
 * @param {Object} options - Options including isPartnerMode and includePartner
 * @returns {Object} Bucket state and functions for managing buckets
 */
const useBuckets = (options = {}) => {
  const { isPartnerMode = false, includePartner = false } = options;
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all buckets
  const refreshBuckets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching all buckets...', { isPartnerMode, includePartner });
      
      // Pass partner access flag and includePartner flag to the service
      const response = await bucketService.getAll({ 
        partnerAccess: isPartnerMode,
        includePartner: includePartner && !isPartnerMode // Only include partner if not in partner mode
      });
      const fetchedBuckets = response.data;
      
      // Ensure we have an array of buckets
      if (!Array.isArray(fetchedBuckets)) {
        console.error('❌ Invalid buckets data:', fetchedBuckets);
        throw new Error('Invalid buckets data received');
      }
      
      // Format and sort buckets
      const formattedBuckets = fetchedBuckets
        .map(formatBucket)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      console.log('✅ Buckets refreshed:', formattedBuckets);
      setBuckets(formattedBuckets);
      return formattedBuckets;
    } catch (err) {
      console.error('❌ Error refreshing buckets:', err);
      setError('Failed to refresh buckets');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isPartnerMode, includePartner]);

  // Load buckets on mount
  useEffect(() => {
    refreshBuckets();
  }, [refreshBuckets]);

  // Create new bucket
  const createBucket = useCallback(async (bucketData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📝 Creating new bucket:', bucketData);
      
      // Validate required fields
      if (!bucketData.name || !bucketData.name.trim()) {
        throw new Error('Bucket name is required');
      }
      
      // Convert type to MongoDB format if needed
      const preparedData = {
        ...bucketData,
        type: bucketData.type === 'HOUSEHOLD' ? 'Household' :
              bucketData.type === 'BUSINESS' ? 'Business' :
              bucketData.type === 'INVESTMENT' ? 'Investment' :
              bucketData.type === 'SIDE_HUSTLE' ? 'Side Hustle' :
              bucketData.type === 'CUSTOM' ? 'Custom' :
              bucketData.type,
        order: bucketData.order ?? buckets.length
      };
      
      console.log('📤 Sending data to API:', preparedData);
      
      const response = await bucketService.create(preparedData);
      
      // Verify the response has the expected data
      if (!response || !response.data) {
        console.error('❌ Invalid server response:', response);
        throw new Error('Invalid response from server when creating bucket');
      }
      
      // Format the new bucket
      const newBucket = formatBucket(response.data);
      
      if (!newBucket) {
        throw new Error('Failed to format bucket data');
      }
      
      console.log('✅ Bucket created successfully:', newBucket);
      
      // Update local state with the new bucket, but don't trigger a refresh
      setBuckets(prevBuckets => {
        // Check if bucket already exists to prevent duplicates
        const exists = prevBuckets.some(b => 
          b._id === newBucket._id || 
          (b.name === newBucket.name && b.type === newBucket.type)
        );
        if (exists) {
          console.log('⚠️ Bucket already exists, skipping state update');
          return prevBuckets;
        }
        console.log('✨ Adding new bucket to state');
        return [...prevBuckets, newBucket];
      });
      
      return newBucket;
    } catch (err) {
      console.error('❌ Error creating bucket:', err);
      setError('Failed to create bucket');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [buckets.length]);

  // Update existing bucket
  const updateBucket = useCallback(async (id, bucketData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔄 Updating bucket ${id}:`, bucketData);
      
      // Convert type to MongoDB format if needed
      const preparedData = {
        ...bucketData,
        // Map type to MongoDB format if needed (reverse of mapBucketType)
        type: bucketData.type === 'HOUSEHOLD' ? 'Household' :
              bucketData.type === 'BUSINESS' ? 'Business' :
              bucketData.type === 'INVESTMENT' ? 'Investment' :
              bucketData.type === 'SIDE_HUSTLE' ? 'Side Hustle' :
              bucketData.type === 'CUSTOM' ? 'Custom' :
              bucketData.type
      };
      
      const response = await bucketService.update(id, preparedData);
      const updatedBucket = formatBucket(response.data);
      
      console.log('✅ Bucket updated successfully:', updatedBucket);
      
      // Update the buckets state with the updated bucket
      setBuckets(prevBuckets => {
        const updatedBuckets = prevBuckets.map(bucket => 
          bucket._id === id ? updatedBucket : bucket
        );
        return updatedBuckets;
      });
      
      // Refresh buckets from storage
      setTimeout(() => {
        refreshBuckets();
      }, 300);
      
      setLoading(false);
      return updatedBucket;
    } catch (err) {
      console.error(`❌ Error updating bucket ${id}:`, err);
      setError('Failed to update bucket');
      setLoading(false);
      throw err;
    }
  }, [refreshBuckets]);

  // Delete bucket
  const deleteBucket = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🗑️ Deleting bucket ${id}`);
      console.log('🧮 Bucket count before deletion:', buckets.length);
      
      // Verify the bucket exists before attempting to delete it
      const bucketExists = buckets.some(bucket => bucket._id === id);
      if (!bucketExists) {
        console.error(`❌ Cannot delete bucket: Bucket with ID ${id} not found`);
        throw new Error(`Bucket with ID ${id} not found`);
      }
      
      // Store bucket info for logging
      const bucketToDelete = buckets.find(bucket => bucket._id === id);
      console.log(`📋 Bucket to delete: ${bucketToDelete?.name || 'Unknown'}`);
      
      // Make the API call to delete the bucket
      console.log('📤 Sending delete request to server');
      const deleteResponse = await bucketService.remove(id);
      
      // Check for a valid response
      if (!deleteResponse || (!deleteResponse.data && !deleteResponse.status)) {
        console.error('❌ Invalid response from delete operation:', deleteResponse);
        throw new Error('Invalid response from server when deleting bucket');
      }
      
      console.log(`✅ Bucket ${id} deleted successfully from server`);
      
      // Update local state immediately
      setBuckets(prevBuckets => {
        const updatedBuckets = prevBuckets.filter(bucket => bucket._id !== id);
        console.log('🧮 Updated bucket count:', updatedBuckets.length);
        return updatedBuckets;
      });
      
      return true;
    } catch (err) {
      console.error(`❌ Error deleting bucket ${id}:`, err);
      setError(err.message || 'Failed to delete bucket');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [buckets]);

  // Reorder buckets
  const reorderBuckets = useCallback(async (reorderedBuckets) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Reordering buckets...');
      
      // Update local state immediately for better UI responsiveness
      setBuckets(reorderedBuckets);
      
      // Prepare updates for the server (just the id and new order)
      const orderUpdates = reorderedBuckets.map((bucket, index) => ({
        id: bucket._id,
        order: index
      }));
      
      // Update buckets on the server
      await bucketService.reorder(orderUpdates);
      
      console.log('✅ Buckets reordered successfully');
      
      // Refresh buckets from storage
      setTimeout(() => {
        refreshBuckets();
      }, 300);
      
      setLoading(false);
      return reorderedBuckets;
    } catch (err) {
      console.error('❌ Error reordering buckets:', err);
      setError('Failed to reorder buckets');
      
      // Refresh buckets to get the original order if there was an error
      refreshBuckets();
      
      setLoading(false);
      throw err;
    }
  }, [refreshBuckets]);

  // Toggle bucket expansion
  const toggleBucketExpansion = useCallback(async (bucketId) => {
    try {
      // Find the bucket to update
      const bucketToUpdate = buckets.find(bucket => bucket._id === bucketId);
      
      if (!bucketToUpdate) {
        console.warn(`⚠️ Bucket not found for toggling: ${bucketId}`);
        return;
      }
      
      // Update local state immediately for responsiveness
      setBuckets(prevBuckets => 
        prevBuckets.map(bucket => 
          bucket._id === bucketId 
            ? { ...bucket, isExpanded: !bucket.isExpanded }
            : bucket
        )
      );
      
      // Update on the server
      await bucketService.update(bucketId, {
        isExpanded: !bucketToUpdate.isExpanded
      });
      
      console.log(`✅ Bucket ${bucketId} expansion toggled to: ${!bucketToUpdate.isExpanded}`);
    } catch (err) {
      console.error(`❌ Error toggling bucket expansion for ${bucketId}:`, err);
      
      // Revert local state change in case of error
      refreshBuckets();
    }
  }, [buckets, refreshBuckets]);

  // Return the hook state and functions
  return {
    buckets,
    loading,
    error,
    createBucket,
    refreshBuckets,
    updateBucket,
    deleteBucket,
    reorderBuckets,
    toggleBucketExpansion
  };
};

export default useBuckets; 